/**
 * Block Service
 * 
 * Business logic for transcript block operations
 */

import {
  generateId,
  splitBlock,
  mergeBlocks,
  deleteBlock,
  duplicateBlock,
  updateBlockText,
  updateBlockSpeaker,
  updateMultipleBlocksSpeaker,
  reorderBlocks,
} from '../../components/TranscriptEditor/utils/transcriptOperations';
import { findBlockIndex } from '../../components/TranscriptEditor/utils/blockIdMatcher';
import { CONFIG } from '../../components/TranscriptEditor/constants/config';

/**
 * Block Service Class
 */
class BlockService {
  /**
   * Split a block at cursor position
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to split
   * @param {number} cursorPosition - Cursor position in text
   * @param {string|null} currentText - Current text from DOM (optional)
   * @returns {Object} Result with new blocks array and focused block ID
   */
  static splitBlock(blocks, blockId, cursorPosition, currentText = null) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex === -1) {
      return { blocks, focusedBlockId: null };
    }

    const block = blocks[blockIndex];
    
    // Use currentText if provided (from DOM), otherwise use block.text (from state)
    const blockToSplit = currentText !== null 
      ? { ...block, text: currentText }
      : block;
    
    const [firstBlock, secondBlock] = splitBlock(blockToSplit, cursorPosition);

    const newBlocks = [
      ...blocks.slice(0, blockIndex),
      firstBlock,
      secondBlock,
      ...blocks.slice(blockIndex + 1),
    ];

    return {
      blocks: newBlocks,
      focusedBlockId: secondBlock.id,
    };
  }

  /**
   * Merge block with previous block
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to merge
   * @returns {Object} Result with new blocks array and focused block ID
   */
  static mergeUp(blocks, blockId) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex <= 0) {
      return { blocks, focusedBlockId: null };
    }

    const currentBlock = blocks[blockIndex];
    const previousBlock = blocks[blockIndex - 1];
    
    // Save the length of the first block's text - this is where cursor should be positioned
    const mergeCursorPosition = previousBlock.text ? previousBlock.text.length : 0;
    
    const mergedBlock = mergeBlocks(previousBlock, currentBlock);
    
    // Add merge cursor position to metadata so we can position cursor correctly
    mergedBlock.metadata = {
      ...mergedBlock.metadata,
      mergeCursorPosition: mergeCursorPosition,
    };

    const newBlocks = [
      ...blocks.slice(0, blockIndex - 1),
      mergedBlock,
      ...blocks.slice(blockIndex + 1),
    ];

    return {
      blocks: newBlocks,
      focusedBlockId: mergedBlock.id,
    };
  }

  /**
   * Merge block with next block
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to merge
   * @returns {Object} Result with new blocks array and focused block ID
   */
  static mergeDown(blocks, blockId) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex === -1 || blockIndex >= blocks.length - 1) {
      return { blocks, focusedBlockId: null };
    }

    const currentBlock = blocks[blockIndex];
    const nextBlock = blocks[blockIndex + 1];
    const mergedBlock = mergeBlocks(currentBlock, nextBlock);

    const newBlocks = [
      ...blocks.slice(0, blockIndex),
      mergedBlock,
      ...blocks.slice(blockIndex + 2),
    ];

    return {
      blocks: newBlocks,
      focusedBlockId: mergedBlock.id,
    };
  }

  /**
   * Delete a block
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to delete
   * @returns {Array} New blocks array
   */
  static delete(blocks, blockId) {
    return deleteBlock(blocks, blockId);
  }

  /**
   * Duplicate a block
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to duplicate
   * @returns {Object} Result with new blocks array and focused block ID
   */
  static duplicate(blocks, blockId) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex === -1) {
      return { blocks, focusedBlockId: null };
    }

    const block = blocks[blockIndex];
    const duplicated = duplicateBlock(block);

    const newBlocks = [
      ...blocks.slice(0, blockIndex + 1),
      duplicated,
      ...blocks.slice(blockIndex + 1),
    ];

    return {
      blocks: newBlocks,
      focusedBlockId: duplicated.id,
    };
  }

  /**
   * Update block text
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to update
   * @param {string} newText - New text content
   * @param {Array|null} syncedWords - Synced words array (optional)
   * @returns {Array} New blocks array
   */
  static updateText(blocks, blockId, newText, syncedWords = null) {
    return updateBlockText(blocks, blockId, newText, syncedWords);
  }

  /**
   * Update block speaker
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to update
   * @param {string} newSpeaker - New speaker name
   * @returns {Array} New blocks array
   */
  static updateSpeaker(blocks, blockId, newSpeaker) {
    return updateBlockSpeaker(blocks, blockId, newSpeaker);
  }

  /**
   * Bulk update speakers for multiple blocks
   * @param {Array} blocks - Current blocks array
   * @param {Array} blockIds - Array of block IDs to update
   * @param {string} newSpeaker - New speaker name
   * @returns {Array} New blocks array
   */
  static bulkUpdateSpeaker(blocks, blockIds, newSpeaker) {
    return updateMultipleBlocksSpeaker(blocks, blockIds, newSpeaker);
  }

  /**
   * Reorder blocks
   * @param {Array} blocks - Current blocks array
   * @param {number} fromIndex - Source index
   * @param {number} toIndex - Destination index
   * @returns {Array} New blocks array
   */
  static reorder(blocks, fromIndex, toIndex) {
    return reorderBlocks(blocks, fromIndex, toIndex);
  }

  /**
   * Move block up
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to move
   * @returns {Array} New blocks array
   */
  static moveUp(blocks, blockId) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex <= 0) {
      return blocks;
    }

    return this.reorder(blocks, blockIndex, blockIndex - 1);
  }

  /**
   * Move block down
   * @param {Array} blocks - Current blocks array
   * @param {string|number} blockId - Block ID to move
   * @returns {Array} New blocks array
   */
  static moveDown(blocks, blockId) {
    const blockIndex = findBlockIndex(blocks, blockId);
    if (blockIndex === -1 || blockIndex >= blocks.length - 1) {
      return blocks;
    }

    return this.reorder(blocks, blockIndex, blockIndex + 1);
  }

  /**
   * Create a new empty block with speaker
   * @param {Array} blocks - Current blocks array
   * @param {string} speakerName - Speaker name for new block
   * @returns {Object} Result with new blocks array and new block ID
   */
  static createEmptyBlock(blocks, speakerName) {
    const lastBlock = blocks[blocks.length - 1];
    const newBlockStartTime = lastBlock ? lastBlock.end : 0;
    
    const newBlock = {
      id: generateId(),
      text: '',
      speaker: speakerName.trim(),
      start: newBlockStartTime,
      end: newBlockStartTime + CONFIG.DEFAULT_BLOCK_DURATION,
      paragraphNumber: blocks.length,
      metadata: {
        isEdited: false,
        lastModified: new Date().toISOString(),
        version: 1,
      },
    };
    
    return {
      blocks: [...blocks, newBlock],
      newBlockId: newBlock.id,
    };
  }

  /**
   * Insert a new empty block between two existing blocks
   * @param {Array} blocks - Current blocks array
   * @param {string|number} afterBlockId - Block ID after which to insert the new block
   * @returns {Object} Result with new blocks array and new block ID
   */
  static insertBlockBetween(blocks, afterBlockId) {
    const blockIndex = findBlockIndex(blocks, afterBlockId);
    if (blockIndex === -1) {
      return { blocks, newBlockId: null };
    }

    const previousBlock = blocks[blockIndex];
    const nextBlock = blocks[blockIndex + 1];
    
    // Calculate start time - use end time of previous block
    const newBlockStartTime = previousBlock.end;
    
    // Calculate end time - if there's a next block, use midpoint, otherwise add default duration
    let newBlockEndTime;
    if (nextBlock) {
      // Use midpoint between previous block end and next block start
      newBlockEndTime = (newBlockStartTime + nextBlock.start) / 2;
    } else {
      // No next block, add default duration
      newBlockEndTime = newBlockStartTime + CONFIG.DEFAULT_BLOCK_DURATION;
    }
    
    const newBlock = {
      id: generateId(),
      text: '',
      speaker: '', // No default speaker - user must select
      start: newBlockStartTime,
      end: newBlockEndTime,
      paragraphNumber: blockIndex + 1,
      metadata: {
        isEdited: false,
        lastModified: new Date().toISOString(),
        version: 1,
      },
    };
    
    // Update paragraph numbers for blocks after the insertion point
    const updatedBlocks = blocks.map((block, idx) => {
      if (idx > blockIndex) {
        return { ...block, paragraphNumber: block.paragraphNumber + 1 };
      }
      return block;
    });
    
    const newBlocks = [
      ...updatedBlocks.slice(0, blockIndex + 1),
      newBlock,
      ...updatedBlocks.slice(blockIndex + 1),
    ];
    
    return {
      blocks: newBlocks,
      newBlockId: newBlock.id,
    };
  }
}

export default BlockService;

